import {
  AdminGenieMeetingFilters,
  AdminGenieMeetingListResponse,
  AdminNotetakerHistoryResponse,
  CalendarProvider,
  GenieCalendarConnection,
  GenieCalendarOption,
  GenieCalendars,
  GenieMeeting,
  GenieMeetingDetail,
  GenieMeetingUsage,
  MeetingNotesParts,
  SendGenieToMeetingPayload,
  UpcomingCalendarEventsResponse,
  UpdateGenieCalendarPayload,
} from "@/types/agent/genie-meeting";
import { ApiService } from "../api/ApiService";

class GenieMeetingService {
  async list(agentId: string): Promise<GenieMeeting[]> {
    const res = await ApiService.invoke<{ meetings: GenieMeeting[] }>({
      resource: "genie-meetings",
      action: "list",
      data: { agentId },
    });
    return res?.meetings ?? [];
  }

  async usage(agentId: string): Promise<GenieMeetingUsage> {
    return ApiService.invoke<GenieMeetingUsage>({
      resource: "genie-meetings",
      action: "usage",
      data: { agentId },
    });
  }

  async get(meetingId: string): Promise<GenieMeetingDetail> {
    const res = await ApiService.invoke<{ meeting: GenieMeetingDetail }>({
      resource: "genie-meetings",
      action: "get",
      id: meetingId,
    });
    return res.meeting;
  }

  async send(agentId: string, payload: SendGenieToMeetingPayload): Promise<GenieMeeting> {
    const res = await ApiService.invoke<{ meeting: GenieMeeting }>({
      resource: "genie-meetings",
      action: "send",
      data: { agentId, ...payload },
    });
    return res.meeting;
  }

  async cancel(meetingId: string): Promise<void> {
    await ApiService.invoke({ resource: "genie-meetings", action: "cancel", id: meetingId });
  }

  async leave(meetingId: string): Promise<void> {
    await ApiService.invoke({ resource: "genie-meetings", action: "leave", id: meetingId });
  }

  async forceStop(meetingId: string): Promise<void> {
    await ApiService.invoke({ resource: "genie-meetings", action: "force-stop", id: meetingId });
  }

  async adminHistory(meetingId: string): Promise<AdminNotetakerHistoryResponse> {
    return ApiService.invoke<AdminNotetakerHistoryResponse>({
      resource: "genie-meetings",
      action: "admin-history",
      id: meetingId,
    });
  }

  async addToKnowledge(meetingId: string, parts: MeetingNotesParts): Promise<GenieMeeting> {
    const res = await ApiService.invoke<{ meeting: GenieMeeting }>({
      resource: "genie-meetings",
      action: "add-to-knowledge",
      id: meetingId,
      data: { parts },
    });
    return res.meeting;
  }

  async adminList(
    filters: AdminGenieMeetingFilters,
    limit: number,
    offset: number,
  ): Promise<AdminGenieMeetingListResponse> {
    const res = await ApiService.invoke<AdminGenieMeetingListResponse>({
      resource: "genie-meetings",
      action: "admin-list",
      data: { ...filters, limit, offset },
    });
    return { meetings: res?.meetings ?? [], count: res?.count ?? 0, limit: res?.limit ?? limit, offset: res?.offset ?? offset };
  }

  async delete(meetingId: string): Promise<void> {
    await ApiService.invoke({ resource: "genie-meetings", action: "delete", id: meetingId });
  }

  async getCalendars(agentId: string): Promise<GenieCalendars> {
    const res = await ApiService.invoke<GenieCalendars>({
      resource: "genie-meetings",
      action: "calendar-get",
      data: { agentId },
    });
    return { connections: res?.connections ?? [], maxConnections: res?.maxConnections ?? 1 };
  }

  /**
   * The Google or Microsoft sign-in link that adds a calendar, or with connectionId
   * connects that one again; it sends the user back to that provider's calendar callback page.
   */
  async connectCalendar(agentId: string, provider: CalendarProvider, connectionId?: string): Promise<string> {
    const res = await ApiService.invoke<{ url: string }>({
      resource: "genie-meetings",
      action: "calendar-connect",
      data: { agentId, provider, connectionId },
    });
    return res.url;
  }

  async completeCalendarConnect(
    code: string,
    state: string,
  ): Promise<{ agentId: string; connection: GenieCalendarConnection }> {
    return ApiService.invoke<{ agentId: string; connection: GenieCalendarConnection }>({
      resource: "genie-meetings",
      action: "calendar-callback",
      data: { code, state },
    });
  }

  async listCalendars(agentId: string, connectionId: string): Promise<GenieCalendarOption[]> {
    const res = await ApiService.invoke<{ calendars: GenieCalendarOption[] }>({
      resource: "genie-meetings",
      action: "calendar-calendars",
      data: { agentId, connectionId },
    });
    return res?.calendars ?? [];
  }

  async selectCalendar(agentId: string, connectionId: string, calendarId: string): Promise<GenieCalendarConnection> {
    const res = await ApiService.invoke<{ connection: GenieCalendarConnection }>({
      resource: "genie-meetings",
      action: "calendar-select",
      data: { agentId, connectionId, calendarId },
    });
    return res.connection;
  }

  async updateCalendar(
    agentId: string,
    connectionId: string,
    payload: UpdateGenieCalendarPayload,
  ): Promise<GenieCalendarConnection> {
    const res = await ApiService.invoke<{ connection: GenieCalendarConnection }>({
      resource: "genie-meetings",
      action: "calendar-update",
      data: { agentId, connectionId, ...payload },
    });
    return res.connection;
  }

  async disconnectCalendar(agentId: string, connectionId: string): Promise<void> {
    await ApiService.invoke({ resource: "genie-meetings", action: "calendar-disconnect", data: { agentId, connectionId } });
  }

  async calendarEvents(agentId: string, connectionId: string): Promise<UpcomingCalendarEventsResponse> {
    const res = await ApiService.invoke<UpcomingCalendarEventsResponse>({
      resource: "genie-meetings",
      action: "calendar-events",
      data: { agentId, connectionId },
    });
    return { events: res?.events ?? [], reconnect: res?.reconnect ?? false };
  }
}

export const genieMeetingService = new GenieMeetingService();
